import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Card, Spinner } from '@easydent/shared-ui';
import { getFeatureDemand, type FeatureDemand } from '@/lib/api';

/**
 * What the public site's visitors have asked us to build.
 *
 * <p>Every feature page ends with a short list of what it does not do yet, each with a button. This
 * is where those clicks land, ranked, so build order can be argued from something other than whoever
 * asked most recently.</p>
 *
 * <p>Counts only. The addresses behind the "notify me" numbers are never sent to this screen —
 * nothing here needs them, and a console that renders a mailing list is one shoulder-surf away from
 * being a breach.</p>
 */
const DemandPage: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<FeatureDemand | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getFeatureDemand()
      .then(setData)
      .catch(() => setError(t('demand.loadError', 'Could not load the demand figures.')));
  }, [t]);

  if (error) return <Alert variant="error">{error}</Alert>;
  if (!data) return <Spinner />;

  const max = Math.max(1, ...data.items.map((i) => i.votes));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-body">{t('demand.title', 'Demand')}</h1>
        <p className="mt-1 text-sm text-muted-strong">
          {t('demand.subtitle', 'What visitors to the public site have asked us to build, most-wanted first.')}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <div className="text-xs uppercase tracking-wide text-muted">{t('demand.totalVotes', 'Requests')}</div>
          <div className="mt-1 text-2xl font-semibold text-body">{data.totalVotes}</div>
        </Card>
        <Card>
          <div className="text-xs uppercase tracking-wide text-muted">{t('demand.subscribers', 'Waiting to hear')}</div>
          <div className="mt-1 text-2xl font-semibold text-body">{data.totalSubscribers}</div>
        </Card>
        <Card>
          <div className="text-xs uppercase tracking-wide text-muted">{t('demand.siteWide', 'Any update')}</div>
          <div className="mt-1 text-2xl font-semibold text-body">{data.siteWideSubscribers}</div>
          <p className="mt-1 text-xs text-muted">
            {t('demand.siteWideNote', 'Left an address without naming a feature.')}
          </p>
        </Card>
      </div>

      <Card>
        {data.items.length === 0 ? (
          <p className="text-sm text-muted-strong">
            {t('demand.empty', 'Nobody has asked for anything yet. The buttons are live on every feature page.')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
                  <th className="pb-2 pr-3 font-medium">{t('demand.feature', 'Feature')}</th>
                  <th className="pb-2 pr-3 font-medium">{t('demand.item', 'Asked for')}</th>
                  <th className="pb-2 pr-3 text-right font-medium">{t('demand.votes', 'Requests')}</th>
                  <th className="pb-2 pr-3 text-right font-medium">{t('demand.emails', 'Emails')}</th>
                  <th className="pb-2 text-right font-medium">{t('demand.last', 'Last')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={`${r.slug}:${r.itemKey}`} className="border-b border-line-soft last:border-0">
                    <td className="py-2 pr-3 text-muted">{r.slug}</td>
                    <td className="py-2 pr-3 font-medium text-body">{r.itemKey}</td>
                    <td className="py-2 pr-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* A bar rather than a sparkline: the only comparison that matters here is
                            between rows, and it has to survive being read at a glance. */}
                        <span className="hidden h-1.5 rounded-full bg-brand-500 sm:block"
                              style={{ width: `${Math.round((r.votes / max) * 80)}px` }} />
                        <span className="font-semibold text-body">{r.votes}</span>
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-right text-muted-strong">{r.subscribers || '—'}</td>
                    <td className="py-2 text-right text-xs text-muted">
                      {r.lastVoteAt ? new Date(r.lastVoteAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-xs text-muted">
        {t('demand.privacyNote',
          'Counts only. The addresses behind the email figures are stored encrypted and are never shown here.')}
      </p>
    </div>
  );
};

export default DemandPage;
